(() => {
  if (customElements.get('article-reader')) return;

  class ArticleReader extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.body = this.querySelector('[data-article-body]');
      this.toc = this.querySelector('[data-article-toc]');
      this.headings = this.body ? Array.from(this.body.querySelectorAll('h2, h3')).filter(heading => heading.textContent.trim()) : [];
      this.links = [];
      if (this.toc && this.headings.length) this.setupContents(signal);
      else if (this.toc) this.toc.hidden = true;

      const shareButton = this.querySelector('[data-article-share]');
      if (shareButton && (navigator.share || navigator.clipboard?.writeText)) {
        shareButton.hidden = false;
        shareButton.addEventListener('click', () => this.share(shareButton), { signal });
      }
    }

    setupContents(signal) {
      const list = this.toc.querySelector('[data-article-toc-list]');
      list.replaceChildren();
      this.headings.forEach((heading, index) => {
        if (!heading.id || document.getElementById(heading.id) !== heading) {
          const base = `article-${this.dataset.articleId}-heading-${index + 1}`;
          let id = base;
          let suffix = 1;
          while (document.getElementById(id) && document.getElementById(id) !== heading) id = `${base}-${suffix++}`;
          heading.id = id;
        }
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${encodeURIComponent(heading.id)}`;
        link.textContent = heading.textContent.trim();
        link.dataset.level = heading.tagName.slice(1);
        link.addEventListener('click', event => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          const hash = `#${encodeURIComponent(heading.id)}`;
          if (location.hash !== hash) history.pushState(null, '', hash);
          if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
          window.scrollTo({
            top: Math.max(0, window.scrollY + heading.getBoundingClientRect().top - this.scrollOffset()),
            behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
          });
          this.activate(index);
        }, { signal });
        item.append(link);
        list.append(item);
        this.links.push(link);
      });
      this.toc.hidden = false;
      const schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.updateActive();
        });
      };
      window.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });
      window.addEventListener('hashchange', schedule, { signal });
      this.updateActive();
    }

    scrollOffset() {
      return (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0) + 32;
    }

    updateActive() {
      let index = 0;
      const threshold = this.scrollOffset() + 24;
      this.headings.forEach((heading, candidate) => {
        if (heading.getBoundingClientRect().top <= threshold) index = candidate;
      });
      this.activate(index);
    }

    activate(index) {
      this.links.forEach((link, candidate) => {
        if (candidate === index) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }

    async share(button) {
      const status = this.querySelector('[data-share-status]');
      const { shareUrl: url, shareTitle: title } = button.dataset;
      status.textContent = '';
      if (navigator.share) {
        try {
          await navigator.share({ title, url });
          return;
        } catch (error) {
          if (error.name === 'AbortError') return;
        }
      }
      try {
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(url);
        status.textContent = 'Link copied';
      } catch (_) {
        status.textContent = 'Unable to copy. Please copy the article link from your address bar.';
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  customElements.define('article-reader', ArticleReader);
})();
